import { useState, useEffect } from 'react'
import {
    Box, Paper, Typography, Button, Grid, Switch, FormControlLabel,
    TextField, CircularProgress, Alert, IconButton, InputAdornment,
    Divider, Slider, Select, MenuItem, FormControl, InputLabel,
    Tabs, Tab, Chip, Table, TableHead, TableBody, TableRow, TableCell,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import { Visibility, VisibilityOff, Save, Send, Palette, Refresh, CloudUpload, Image, Delete, Edit, Add, Label } from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import ConfirmDialog from '@components/Common/ConfirmDialog'
import { useUIStore, DEFAULT_THEME_SETTINGS } from '@store/uiStore'
import { FONT_FAMILIES } from '@styles/theme'
import LanguageSwitcher from '@components/Common/LanguageSwitcher'
import SystemUsers from '@components/Settings/SystemUsers'
import { toast } from 'react-toastify'
import axios from '@api/axios'

// Color input component
const ColorPicker = ({ label, value, onChange, language }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
                component="input"
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{
                    width: 50,
                    height: 40,
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    padding: 0,
                    '&::-webkit-color-swatch': {
                        borderRadius: 4,
                        border: 'none',
                    },
                    '&::-webkit-color-swatch-wrapper': {
                        padding: 2,
                    }
                }}
            />
            <TextField
                size="small"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ style: { fontFamily: 'monospace' } }}
            />
        </Box>
    </Box>
)

const Settings = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const {
        darkMode,
        toggleDarkMode,
        themeSettings,
        setThemeSetting,
        setThemeSettings,
        resetThemeSettings
    } = useUIStore()

    const isAdmin = user?.user_type === 'admin'
    const [activeTab, setActiveTab] = useState(0)

    // SMTP State
    const [smtpConfig, setSmtpConfig] = useState({
        host: '',
        port: '587',
        username: '',
        password: '',
        from_email: '',
        from_name: 'Form Management System',
        encryption: 'tls'
    })
    const [smtpLoaded, setSmtpLoaded] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [logoUploading, setLogoUploading] = useState(false)
    const [faviconUploading, setFaviconUploading] = useState(false)

    // Categories State
    const [categories, setCategories] = useState([])
    const [categoryForms, setCategoryForms] = useState([])
    const [selectedFormId, setSelectedFormId] = useState('')
    const [categoriesLoading, setCategoriesLoading] = useState(false)
    const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', data: null })
    const [categoryForm, setCategoryForm] = useState({ name_en: '', name_ar: '', color: '#1a3a6b', sort_order: 0 })
    const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' })

    // Load forms for category management
    useEffect(() => {
        if (isAdmin) {
            axios.get('/forms').then(res => {
                const forms = res.data?.data || res.data || []
                setCategoryForms(forms)
            }).catch(() => { })
        }
    }, [isAdmin])

    // Load categories when form is selected
    useEffect(() => {
        if (selectedFormId) {
            setCategoriesLoading(true)
            axios.get(`/categories?form_id=${selectedFormId}`)
                .then(res => setCategories(res.data?.data || []))
                .catch(() => { })
                .finally(() => setCategoriesLoading(false))
        }
    }, [selectedFormId])

    const handleOpenCategoryDialog = (mode = 'create', cat = null) => {
        setCategoryDialog({ open: true, mode, data: cat })
        if (mode === 'edit' && cat) {
            setCategoryForm({ name_en: cat.name_en, name_ar: cat.name_ar, color: cat.color, sort_order: cat.sort_order || 0 })
        } else {
            setCategoryForm({ name_en: '', name_ar: '', color: '#1a3a6b', sort_order: 0 })
        }
    }

    const handleSaveCategory = async () => {
        if (!categoryForm.name_en || !categoryForm.name_ar) {
            toast.error(language === 'ar' ? 'يرجى إدخال الاسم باللغتين' : 'Please enter name in both languages')
            return
        }
        try {
            if (categoryDialog.mode === 'edit' && categoryDialog.data) {
                await axios.put(`/categories/${categoryDialog.data.id}`, categoryForm)
                toast.success(language === 'ar' ? 'تم تحديث التصنيف' : 'Category updated')
            } else {
                await axios.post('/categories', { ...categoryForm, form_id: selectedFormId })
                toast.success(language === 'ar' ? 'تم إنشاء التصنيف' : 'Category created')
            }
            setCategoryDialog({ open: false, mode: 'create', data: null })
            // Refresh
            const res = await axios.get(`/categories?form_id=${selectedFormId}`)
            setCategories(res.data?.data || [])
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error')
        }
    }

    const handleDeleteCategory = async (catId) => {
        try {
            await axios.delete(`/categories/${catId}`)
            setCategories(prev => prev.filter(c => c.id !== catId))
            toast.success(language === 'ar' ? 'تم حذف التصنيف' : 'Category deleted')
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error')
        }
        setConfirmDelete({ open: false, id: null, name: '' })
    }

    const handleRequestDeleteCategory = (cat) => {
        const name = language === 'ar' ? cat.name_ar : cat.name_en
        setConfirmDelete({ open: true, id: cat.id, name })
    }

    const handleToggleCategory = async (cat) => {
        try {
            await axios.put(`/categories/${cat.id}`, { is_active: !cat.is_active })
            setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, is_active: !c.is_active } : c))
        } catch (err) {
            toast.error('Error toggling category')
        }
    }

    // Handle logo upload
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error(language === 'ar' ? 'يرجى اختيار ملف صورة' : 'Please select an image file')
            return
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error(language === 'ar' ? 'حجم الملف كبير جداً (الحد الأقصى 2MB)' : 'File size too large (max 2MB)')
            return
        }

        setLogoUploading(true)
        try {
            const formData = new FormData()
            formData.append('file', file)

            const res = await axios.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            if (res.data?.url) {
                // Update local store
                setThemeSetting('logoUrl', res.data.url)

                // Save to API so it persists across all sessions
                await axios.post('/settings/theme', {
                    ...themeSettings,
                    logoUrl: res.data.url  // Must come AFTER spread to overwrite old logoUrl
                })

                toast.success(language === 'ar' ? 'تم رفع الشعار بنجاح' : 'Logo uploaded successfully')
            }
        } catch (err) {
            toast.error(language === 'ar' ? 'فشل في رفع الشعار' : 'Failed to upload logo')
        } finally {
            setLogoUploading(false)
        }
    }

    // Handle favicon upload
    const handleFaviconUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            toast.error(language === 'ar' ? 'يرجى اختيار ملف صورة' : 'Please select an image file')
            return
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error(language === 'ar' ? 'حجم الملف كبير جداً (الحد الأقصى 2MB)' : 'File size too large (max 2MB)')
            return
        }

        setFaviconUploading(true)
        try {
            const formData = new FormData()
            formData.append('file', file)

            const res = await axios.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            if (res.data?.url) {
                setThemeSetting('faviconUrl', res.data.url)

                await axios.post('/settings/theme', {
                    ...themeSettings,
                    faviconUrl: res.data.url
                })

                toast.success(language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Favicon uploaded successfully')
            }
        } catch (err) {
            toast.error(language === 'ar' ? 'فشل في رفع الأيقونة' : 'Failed to upload favicon')
        } finally {
            setFaviconUploading(false)
        }
    }

    // Debug: Log whenever smtpConfig changes
    useEffect(() => {
        console.log('smtpConfig state updated:', smtpConfig)
    }, [smtpConfig])

    // Load current SMTP settings
    useEffect(() => {
        console.log('SMTP useEffect running, isAdmin:', isAdmin, 'user:', !!user)
        if (isAdmin && user) {
            console.log('Fetching SMTP settings...')
            axios.get('/settings/smtp')
                .then(res => {
                    console.log('SMTP API response:', res.data)
                    // Handle both response structures: res.data.data or res.data directly
                    const data = res.data?.data || res.data
                    if (data && data.host) {
                        console.log('Setting smtpConfig to:', data)
                        setSmtpConfig({
                            host: data.host || '',
                            port: data.port || '587',
                            username: data.username || '',
                            password: data.password || '',
                            from_email: data.from_email || '',
                            from_name: data.from_name || 'Form Management System',
                            encryption: data.encryption || 'tls'
                        })
                    }
                })
                .catch((err) => {
                    console.error('Failed to load SMTP settings:', err)
                })
                .finally(() => {
                    setSmtpLoaded(true)  // Always set loaded after fetch completes
                })
        }
    }, [isAdmin, user])

    const handleSmtpChange = (field, value) => {
        setSmtpConfig(prev => ({ ...prev, [field]: value }))
        setTestResult(null)
    }

    const handleSaveSmtp = async () => {
        setSaving(true)
        try {
            await axios.post('/settings/smtp', smtpConfig)
            toast.success(language === 'ar' ? 'تم حفظ الإعدادات' : 'Settings saved successfully')
        } catch (err) {
            toast.error(language === 'ar' ? 'فشل في حفظ الإعدادات' : 'Failed to save settings')
        } finally {
            setSaving(false)
        }
    }

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const res = await axios.post('/email/test', smtpConfig)
            setTestResult({ success: true, message: res.data?.message || 'Connection successful!' })
        } catch (err) {
            setTestResult({
                success: false,
                message: err.response?.data?.message || 'Connection failed'
            })
        } finally {
            setTesting(false)
        }
    }

    const handleResetTheme = () => {
        resetThemeSettings()
        toast.success(language === 'ar' ? 'تم إعادة تعيين السمة' : 'Theme reset to defaults')
    }

    return (
        <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                {t('settings.title')}
            </Typography>

            {/* Tabs for settings sections */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{ px: 2 }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label={language === 'ar' ? 'عام' : 'General'} />
                    <Tab label={language === 'ar' ? 'تخصيص السمة' : 'Theme Customization'} />
                    {isAdmin && <Tab label={language === 'ar' ? 'إعدادات SMTP' : 'SMTP Settings'} />}
                    {isAdmin && <Tab label={language === 'ar' ? 'مستخدمي النظام' : 'System Users'} />}
                    {isAdmin && <Tab label={language === 'ar' ? 'التصنيفات' : 'Categories'} icon={<Label sx={{ fontSize: 18 }} />} iconPosition="start" />}
                </Tabs>
            </Paper>

            {/* General Settings Tab */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {t('settings.general')}
                            </Typography>

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('settings.theme')}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={darkMode}
                                            onChange={toggleDarkMode}
                                        />
                                    }
                                    label={darkMode ? t('settings.darkMode') : t('settings.lightMode')}
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('settings.language')}
                                </Typography>
                                <LanguageSwitcher variant="button" />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Theme Customization Tab */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    {/* Colors */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Palette color="primary" />
                                    {language === 'ar' ? 'الألوان' : 'Colors'}
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<Refresh />}
                                    onClick={handleResetTheme}
                                >
                                    {language === 'ar' ? 'إعادة تعيين' : 'Reset'}
                                </Button>
                            </Box>

                            <ColorPicker
                                label={language === 'ar' ? 'اللون الأساسي' : 'Primary Color'}
                                value={themeSettings.primaryColor}
                                onChange={(v) => setThemeSetting('primaryColor', v)}
                                language={language}
                            />

                            <ColorPicker
                                label={language === 'ar' ? 'اللون الثانوي' : 'Secondary Color'}
                                value={themeSettings.secondaryColor}
                                onChange={(v) => setThemeSetting('secondaryColor', v)}
                                language={language}
                            />

                            <ColorPicker
                                label={language === 'ar' ? 'لون النجاح' : 'Success Color'}
                                value={themeSettings.successColor}
                                onChange={(v) => setThemeSetting('successColor', v)}
                                language={language}
                            />

                            <ColorPicker
                                label={language === 'ar' ? 'لون التحذير' : 'Warning Color'}
                                value={themeSettings.warningColor}
                                onChange={(v) => setThemeSetting('warningColor', v)}
                                language={language}
                            />

                            <ColorPicker
                                label={language === 'ar' ? 'لون الخطأ' : 'Error Color'}
                                value={themeSettings.errorColor}
                                onChange={(v) => setThemeSetting('errorColor', v)}
                                language={language}
                            />
                        </Paper>
                    </Grid>

                    {/* Logo Upload */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Image color="primary" />
                                {language === 'ar' ? 'شعار النظام' : 'System Logo'}
                            </Typography>

                            {/* Logo Preview */}
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 3,
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    bgcolor: 'background.default'
                                }}
                            >
                                {themeSettings.logoUrl ? (
                                    <Box
                                        component="img"
                                        src={themeSettings.logoUrl}
                                        alt="Logo"
                                        sx={{
                                            maxWidth: 150,
                                            maxHeight: 100,
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ color: 'text.secondary' }}>
                                        <CloudUpload sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography>
                                            {language === 'ar' ? 'لا يوجد شعار' : 'No logo uploaded'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Upload Button */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={logoUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                                    disabled={logoUploading}
                                >
                                    {language === 'ar' ? 'رفع شعار' : 'Upload Logo'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                    />
                                </Button>

                                {themeSettings.logoUrl && themeSettings.logoUrl !== '/moj-logo.png' && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => setThemeSetting('logoUrl', '/moj-logo.png')}
                                    >
                                        {language === 'ar' ? 'استعادة الافتراضي' : 'Reset to Default'}
                                    </Button>
                                )}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                {language === 'ar'
                                    ? 'الحجم الموصى به: 150×100 بكسل. الحد الأقصى: 2MB'
                                    : 'Recommended size: 150×100px. Max: 2MB'}
                            </Typography>
                        </Paper>

                        {/* Favicon Upload */}
                        <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Image color="primary" />
                                {language === 'ar' ? 'أيقونة المتصفح (Favicon)' : 'Browser Icon (Favicon)'}
                            </Typography>

                            <Box
                                sx={{
                                    mb: 3,
                                    p: 3,
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    bgcolor: 'background.default'
                                }}
                            >
                                {themeSettings.faviconUrl ? (
                                    <Box
                                        component="img"
                                        src={themeSettings.faviconUrl}
                                        alt="Favicon"
                                        sx={{
                                            maxWidth: 64,
                                            maxHeight: 64,
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ color: 'text.secondary' }}>
                                        <CloudUpload sx={{ fontSize: 32, mb: 1 }} />
                                        <Typography>
                                            {language === 'ar' ? 'لا توجد أيقونة' : 'No favicon uploaded'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={faviconUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                                    disabled={faviconUploading}
                                >
                                    {language === 'ar' ? 'رفع أيقونة' : 'Upload Favicon'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/png, image/x-icon, image/jpeg, image/svg+xml"
                                        onChange={handleFaviconUpload}
                                    />
                                </Button>

                                {themeSettings.faviconUrl && themeSettings.faviconUrl !== '/moj-logo.png' && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => setThemeSetting('faviconUrl', '/moj-logo.png')}
                                    >
                                        {language === 'ar' ? 'استعادة الافتراضي' : 'Reset to Default'}
                                    </Button>
                                )}
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                {language === 'ar'
                                    ? 'الحجم الموصى به: 32×32 بكسل (png, ico, svg). الحد الأقصى: 2MB'
                                    : 'Recommended size: 32×32px (png, ico, svg). Max: 2MB'}
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* System Identity */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                {language === 'ar' ? '🏛️ هوية النظام' : '🏛️ System Identity'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {language === 'ar'
                                    ? 'تخصيص اسم النظام الذي يظهر في شريط العنوان والشريط الجانبي'
                                    : 'Customize the system name displayed in the header navbar and sidebar'}
                            </Typography>

                            <TextField
                                fullWidth
                                size="small"
                                label={language === 'ar' ? 'اسم النظام (إنجليزي)' : 'System Name (English)'}
                                value={themeSettings.systemNameEn || ''}
                                onChange={(e) => setThemeSetting('systemNameEn', e.target.value)}
                                placeholder="Ministry of Justice"
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                size="small"
                                label={language === 'ar' ? 'اسم النظام (عربي)' : 'System Name (Arabic)'}
                                value={themeSettings.systemNameAr || ''}
                                onChange={(e) => setThemeSetting('systemNameAr', e.target.value)}
                                placeholder="وزارة العدل"
                                dir="rtl"
                                sx={{ mb: 2 }}
                            />

                            <Alert severity="info" sx={{ mt: 2 }}>
                                {language === 'ar'
                                    ? 'سيظهر هذا الاسم في رأس الصفحة والشريط الجانبي'
                                    : 'This name will appear in the header and sidebar caption'}
                            </Alert>
                        </Paper>
                    </Grid>

                    {/* Typography & Shape */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {language === 'ar' ? 'الخط والشكل' : 'Typography & Shape'}
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>{language === 'ar' ? 'نوع الخط' : 'Font Family'}</InputLabel>
                                <Select
                                    value={themeSettings.fontFamily}
                                    label={language === 'ar' ? 'نوع الخط' : 'Font Family'}
                                    onChange={(e) => setThemeSetting('fontFamily', e.target.value)}
                                >
                                    {FONT_FAMILIES.map((font) => (
                                        <MenuItem
                                            key={font.value}
                                            value={font.value}
                                            sx={{ fontFamily: font.stack }}
                                        >
                                            {font.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {language === 'ar' ? 'نصف قطر الحدود' : 'Border Radius'}: {themeSettings.borderRadius}px
                                </Typography>
                                <Slider
                                    value={themeSettings.borderRadius}
                                    onChange={(_, v) => setThemeSetting('borderRadius', v)}
                                    min={0}
                                    max={24}
                                    step={2}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 8, label: '8' },
                                        { value: 16, label: '16' },
                                        { value: 24, label: '24' },
                                    ]}
                                />
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Preview */}
                            <Typography variant="h6" gutterBottom>
                                {language === 'ar' ? 'معاينة' : 'Preview'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                <Button variant="contained" color="primary">
                                    {language === 'ar' ? 'أساسي' : 'Primary'}
                                </Button>
                                <Button variant="contained" color="secondary">
                                    {language === 'ar' ? 'ثانوي' : 'Secondary'}
                                </Button>
                                <Button variant="contained" color="success">
                                    {language === 'ar' ? 'نجاح' : 'Success'}
                                </Button>
                                <Button variant="contained" color="warning">
                                    {language === 'ar' ? 'تحذير' : 'Warning'}
                                </Button>
                                <Button variant="contained" color="error">
                                    {language === 'ar' ? 'خطأ' : 'Error'}
                                </Button>
                            </Box>
                            <TextField
                                fullWidth
                                label={language === 'ar' ? 'حقل نصي' : 'Text Field'}
                                placeholder={language === 'ar' ? 'اكتب هنا...' : 'Type here...'}
                                sx={{ mb: 2 }}
                            />
                            <Alert severity="info" sx={{ mb: 1 }}>
                                {language === 'ar' ? 'هذه معاينة للسمة المخصصة' : 'This is a preview of your custom theme'}
                            </Alert>
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={async () => {
                                        try {
                                            await axios.post('/settings/theme', themeSettings);
                                            toast.success(language === 'ar' ? 'تم حفظ السمة بنجاح' : 'Theme saved successfully');
                                        } catch (err) {
                                            toast.error(language === 'ar' ? 'فشل في الحفظ' : 'Failed to save theme');
                                        }
                                    }}
                                >
                                    {language === 'ar' ? 'حفظ السمة' : 'Save Theme'}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* SMTP Settings Tab (Admin only) */}
            {activeTab === 2 && isAdmin && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {t('settings.smtp')}
                                {!smtpLoaded && <CircularProgress size={16} sx={{ ml: 1 }} />}
                            </Typography>

                            {/* Only render form fields after data is loaded to prevent browser autofill */}
                            {smtpLoaded ? (
                                <Box
                                    component="form"
                                    autoComplete="off"
                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
                                >
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={language === 'ar' ? 'خادم SMTP' : 'SMTP Host'}
                                        placeholder="smtp.gmail.com"
                                        value={smtpConfig.host}
                                        onChange={(e) => handleSmtpChange('host', e.target.value)}
                                        autoComplete="off"
                                        name={`smtp_host_${Date.now()}`}
                                        inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-form-type': 'other' }}
                                    />

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={language === 'ar' ? 'المنفذ' : 'Port'}
                                                placeholder="587"
                                                value={smtpConfig.port}
                                                onChange={(e) => handleSmtpChange('port', e.target.value)}
                                                autoComplete="off"
                                                inputProps={{ autoComplete: 'off', 'data-lpignore': 'true' }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={language === 'ar' ? 'التشفير' : 'Encryption'}
                                                select
                                                SelectProps={{ native: true }}
                                                value={smtpConfig.encryption}
                                                onChange={(e) => handleSmtpChange('encryption', e.target.value)}
                                            >
                                                <option value="tls">TLS</option>
                                                <option value="ssl">SSL</option>
                                                <option value="none">None</option>
                                            </TextField>
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={language === 'ar' ? 'اسم المستخدم' : 'Username'}
                                        placeholder="your-email@gmail.com"
                                        value={smtpConfig.username}
                                        onChange={(e) => handleSmtpChange('username', e.target.value)}
                                        autoComplete="off"
                                        name={`smtp_user_${Date.now()}`}
                                        inputProps={{ autoComplete: 'off', 'data-lpignore': 'true', 'data-form-type': 'other' }}
                                    />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={language === 'ar' ? 'كلمة المرور' : 'Password'}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="App Password"
                                        value={smtpConfig.password}
                                        onChange={(e) => handleSmtpChange('password', e.target.value)}
                                        autoComplete="new-password"
                                        name={`smtp_pass_${Date.now()}`}
                                        inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true' }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setShowPassword(!showPassword)}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    <Divider sx={{ my: 1 }} />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={language === 'ar' ? 'البريد المرسل' : 'From Email'}
                                        placeholder="noreply@yourdomain.com"
                                        value={smtpConfig.from_email || ''}
                                        onChange={(e) => handleSmtpChange('from_email', e.target.value)}
                                        autoComplete="off"
                                    />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={language === 'ar' ? 'اسم المرسل' : 'From Name'}
                                        placeholder="Form Management System"
                                        value={smtpConfig.from_name || ''}
                                        onChange={(e) => handleSmtpChange('from_name', e.target.value)}
                                        autoComplete="off"
                                    />

                                    {testResult && (
                                        <Alert severity={testResult.success ? 'success' : 'error'}>
                                            {testResult.message}
                                        </Alert>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                                            onClick={handleSaveSmtp}
                                            disabled={saving}
                                        >
                                            {language === 'ar' ? 'حفظ' : 'Save'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={testing ? <CircularProgress size={16} /> : <Send />}
                                            onClick={handleTestConnection}
                                            disabled={testing || !smtpConfig.host}
                                        >
                                            {t('settings.testConnection')}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* System Users Tab (Admin only) */}
            {activeTab === 3 && isAdmin && (
                <SystemUsers />
            )}

            {/* Categories Management Tab (Admin only) */}
            {activeTab === 4 && isAdmin && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Label color="primary" />
                                    {language === 'ar' ? 'إدارة التصنيفات' : 'Manage Categories'}
                                </Typography>
                            </Box>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>{language === 'ar' ? 'اختر النموذج' : 'Select Form'}</InputLabel>
                                <Select
                                    value={selectedFormId}
                                    label={language === 'ar' ? 'اختر النموذج' : 'Select Form'}
                                    onChange={(e) => setSelectedFormId(e.target.value)}
                                >
                                    {categoryForms.map(f => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {language === 'ar' ? (f.title_ar || f.title) : (f.title || f.title_en)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {selectedFormId && (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            onClick={() => handleOpenCategoryDialog('create')}
                                        >
                                            {language === 'ar' ? 'تصنيف جديد' : 'New Category'}
                                        </Button>
                                    </Box>

                                    {categoriesLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : categories.length === 0 ? (
                                        <Alert severity="info">
                                            {language === 'ar' ? 'لا توجد تصنيفات لهذا النموذج' : 'No categories for this form yet'}
                                        </Alert>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{language === 'ar' ? 'اللون' : 'Color'}</TableCell>
                                                    <TableCell>{language === 'ar' ? 'الاسم (إنجليزي)' : 'Name (EN)'}</TableCell>
                                                    <TableCell>{language === 'ar' ? 'الاسم (عربي)' : 'Name (AR)'}</TableCell>
                                                    <TableCell>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                                                    <TableCell>{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {categories.map(cat => (
                                                    <TableRow key={cat.id}>
                                                        <TableCell>
                                                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: cat.color, border: '2px solid', borderColor: 'divider' }} />
                                                        </TableCell>
                                                        <TableCell>{cat.name_en}</TableCell>
                                                        <TableCell dir="rtl">{cat.name_ar}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={cat.is_active ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'معطل' : 'Inactive')}
                                                                size="small"
                                                                color={cat.is_active ? 'success' : 'default'}
                                                                onClick={() => handleToggleCategory(cat)}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton size="small" onClick={() => handleOpenCategoryDialog('edit', cat)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" color="error" onClick={() => handleRequestDeleteCategory(cat)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Category Create/Edit Dialog */}
            <Dialog open={categoryDialog.open} onClose={() => setCategoryDialog({ open: false, mode: 'create', data: null })} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {categoryDialog.mode === 'edit'
                        ? (language === 'ar' ? 'تعديل التصنيف' : 'Edit Category')
                        : (language === 'ar' ? 'تصنيف جديد' : 'New Category')}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField
                        label={language === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'}
                        fullWidth
                        value={categoryForm.name_en}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name_en: e.target.value }))}
                    />
                    <TextField
                        label={language === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)'}
                        fullWidth
                        dir="rtl"
                        value={categoryForm.name_ar}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, name_ar: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle2">{language === 'ar' ? 'اللون' : 'Color'}:</Typography>
                        <Box
                            component="input"
                            type="color"
                            value={categoryForm.color}
                            onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                            sx={{ width: 50, height: 40, border: '2px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', p: 0 }}
                        />
                        <Chip
                            label={categoryForm.name_en || 'Preview'}
                            sx={{ bgcolor: categoryForm.color + '20', color: categoryForm.color, fontWeight: 600, border: `1px solid ${categoryForm.color}40` }}
                        />
                    </Box>
                    <TextField
                        label={language === 'ar' ? 'الترتيب' : 'Sort Order'}
                        type="number"
                        value={categoryForm.sort_order}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        sx={{ width: 150 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialog({ open: false, mode: 'create', data: null })}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button variant="contained" onClick={handleSaveCategory}>
                        {language === 'ar' ? 'حفظ' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDelete.open}
                title={language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                message={language === 'ar' ? `هل أنت متأكد من حذف التصنيف "${confirmDelete.name}"؟` : `Delete category "${confirmDelete.name}"?`}
                confirmText={language === 'ar' ? 'حذف' : 'Delete'}
                cancelText={language === 'ar' ? 'إلغاء' : 'Cancel'}
                onConfirm={() => handleDeleteCategory(confirmDelete.id)}
                onCancel={() => setConfirmDelete({ open: false, id: null, name: '' })}
            />
        </Box>
    )
}

export default Settings
